// src/components/admin/DesktopBuilds.jsx
//
// The installer uploader inside the tool form. It talks to the server on its
// own rather than through the form's submit: a 200 MB .dmg has no business
// riding along with a name change, and an admin who uploads one and then hits
// Cancel should still have uploaded it.

import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MonitorDown, Trash2, Upload } from 'lucide-react';
import { useTools } from '../../hooks/useTools';
import { useDesktopPlatforms } from '../../hooks/useDesktopPlatforms';
import { formatSize } from '../../services/desktop';
import { Button, Field, IconButton, Spinner, TextInput, cx } from '../common/ui';

// "It could not be uploaded" is useless to whoever has to fix it. An installer
// upload has a handful of distinct failure modes and they need different
// actions, so say which one happened — the server's own words when it sent
// any, and otherwise what the status code actually means here.
const describeFailure = (err) => {
  const said = err.response?.data?.error;
  if (said) return said;

  // No response at all: the request never completed a round trip. Nearly
  // always the API being unreachable, CORS, or a proxy hanging up mid-body.
  if (!err.response) {
    return 'The upload never reached the server. Check that the API is running and reachable from this site.';
  }

  switch (err.response.status) {
    case 413:
      return 'A proxy in front of the API refused the upload as too large — raise client_max_body_size (nginx) or the equivalent.';
    case 404:
      return 'This API has no desktop-build endpoint, so it is running a build without the desktop-download feature.';
    case 401:
      return 'Your session expired during the upload. Sign in again and retry.';
    case 403:
      return 'Only an admin can upload installers.';
    default:
      return `The server rejected the upload (HTTP ${err.response.status}).`;
  }
};

const PlatformRow = ({ platform, build, toolId, maxUploadBytes, onChanged }) => {
  const { uploadDesktopBuild, removeDesktopBuild } = useTools(true);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState(build?.version || '');
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const isUploading = progress !== null;

  const reset = () => {
    setFile(null);
    setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const upload = async () => {
    // Fail in the file picker rather than after pushing 600 MB up the wire.
    if (maxUploadBytes && file.size > maxUploadBytes) {
      toast.error(
        `${file.name} is ${formatSize(file.size)} — the limit is ${formatSize(maxUploadBytes)}.`
      );
      return;
    }

    setProgress(0);
    try {
      const updated = await uploadDesktopBuild({
        toolId,
        platform: platform.key,
        file,
        version,
        onProgress: setProgress,
      });
      onChanged(updated);
      toast.success(`${platform.label} build uploaded`);
      reset();
    } catch (err) {
      toast.error(describeFailure(err), { duration: 8000 });
      console.error('Desktop build upload failed:', err.response?.status, err);
      setProgress(null);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the ${platform.label} installer? Nobody will be able to download it.`)) {
      return;
    }
    try {
      const updated = await removeDesktopBuild({ toolId, platform: platform.key });
      onChanged(updated);
      setVersion('');
      toast.success(`${platform.label} build removed`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not remove that build');
    }
  };

  return (
    <div className="rounded-lg border border-line bg-surface-2 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-[13px] font-medium">
          <MonitorDown className="h-4 w-4 text-ink-3" />
          {platform.label}
        </span>
        <span className="font-mono text-[11px] text-ink-3">{platform.extensions.join(' / ')}</span>
      </div>

      {build && (
        <div className="mt-2.5 flex items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px]">{build.fileName}</p>
            <p className="mt-0.5 text-xs text-ink-3">
              {[build.version && `v${build.version}`, formatSize(build.size),
                build.uploadedAt && new Date(build.uploadedAt).toLocaleDateString()]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <IconButton label={`Remove the ${platform.label} build`} tone="danger" onClick={remove}>
            <Trash2 className="h-4 w-4" />
          </IconButton>
        </div>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={platform.extensions.join(',')}
          disabled={isUploading}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className={cx(
            'min-w-0 flex-1 text-[13px] text-ink-2',
            'file:mr-2.5 file:rounded-md file:border file:border-line-strong file:bg-surface',
            'file:px-2.5 file:py-1.5 file:text-[13px] file:text-ink hover:file:bg-surface-3'
          )}
        />

        {file && (
          <>
            <TextInput
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.2.0"
              aria-label={`${platform.label} version`}
              disabled={isUploading}
              className="w-24 py-1.5 text-[13px]"
            />
            <Button variant="primary" onClick={upload} disabled={isUploading}>
              <Upload className="h-4 w-4" />
              {build ? 'Replace' : 'Upload'}
            </Button>
          </>
        )}
      </div>

      {isUploading && (
        <div className="mt-2.5">
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
            <div
              className="h-full rounded-full bg-accent transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-ink-3">
            {progress < 100 ? `Uploading… ${progress}%` : 'Finishing up…'}
          </p>
        </div>
      )}
    </div>
  );
};

const DesktopBuilds = ({ tool }) => {
  const { platforms, maxUploadBytes, isLoading } = useDesktopPlatforms();
  // The form's `tool` prop is a snapshot taken when the modal opened, so the
  // rows track their own copy of the builds from each upload's response.
  const [builds, setBuilds] = useState(() => tool?.desktopBuilds || {});

  if (isLoading) return <Spinner label="Loading platforms…" />;

  return (
    <Field
      label="Desktop app"
      hint="Optional. Upload an installer and the tool asks people whether to open it in the browser or download it."
    >
      <div className="flex flex-col gap-2">
        {platforms.map((platform) => (
          <PlatformRow
            key={platform.key}
            platform={platform}
            build={builds[platform.key]}
            toolId={tool._id}
            maxUploadBytes={maxUploadBytes}
            onChanged={(updated) => setBuilds(updated?.desktopBuilds || {})}
          />
        ))}
      </div>
    </Field>
  );
};

export default DesktopBuilds;
